import { logger } from '@belong/logging';
import { Entry } from '@belong/types';
import { CONFIG } from '../target-config';
import { FAdobeTargetActivity } from './factories';
import { requestAdobeTargetActivity } from './requestTargetActivity';
import { selfTerminate } from './selfTermination';
import {
  IAdobeTargetActivityResponse,
  IAdobeTargetExperienceContent,
  IAdobeTargetRedirect,
  IFindAdobeTargetActivity,
  IGetExperienceSections,
  isIAdobeTargetActivityError,
  IUpdateSectionsWithAdobeTargetActivity,
  IUpdateSectionsWithAdobeTargetActivityResult
} from './types';

export const API_ERROR = 'Error getting the Target Activity from Adobe';

// grab the mbox names from the activities and get the Target Activity from Adobe
// we're only dealing with one target at this time although can be used to make multiple changes on the page
export const findAdobeTargetActivity = async ({
  ctx,
  sessionId,
  supplementalDataId,
  unparsedPageEntry,
  additionalMboxLocation,
  isAuthenticatedCustomer,
  forceControlExperience
}: IFindAdobeTargetActivity): Promise<Record<string, IAdobeTargetActivityResponse> | undefined> => {
  const unparsedEntries = unparsedPageEntry?.fields.sections ?? [];
  const terminationTimer = Number(CONFIG.timeout ?? 200);

  // target sections will be an array of sections that have an mboxName
  const activitySections = findActivitySections(unparsedEntries);
  const sectionMboxLocations = activitySections.map(section => section.fields.mBoxName);
  const mboxLocations = Array.from(
    new Set(additionalMboxLocation ? [...sectionMboxLocations, additionalMboxLocation] : sectionMboxLocations)
  );

  const requestAdobeTargetActivityStart = Date.now();

  if (!mboxLocations.length) {
    // return undefined if there are no mboxes to request
    return undefined;
  }

  const requestStartedAt = Date.now();
  const adobeTargetActivityResponseMap: Record<string, IAdobeTargetActivityResponse> = {};

  try {
    const response = await selfTerminate(
      requestAdobeTargetActivity({
        ctx,
        mboxLocations,
        sessionId,
        supplementalDataId,
        isAuthenticatedCustomer,
        forceControlExperience // this lets Adobe target know that the user accessing the page is a control/excluded group
      }),
      terminationTimer
    );

    if (!response) {
      return undefined;
    }

    const executionTime = Date.now() - requestStartedAt;

    mboxLocations.forEach(mboxLocation => {
      const matchedMbox = response.execute?.mboxes?.find(mbox => mbox.name === mboxLocation);

      adobeTargetActivityResponseMap[mboxLocation] = {
        ...response,
        execute: {
          ...response.execute,
          mboxes: matchedMbox ? [matchedMbox] : []
        },
        executionTime
      };
    });
  } catch (error) {
    const executionTime = Date.now() - requestStartedAt;
    logger.error(API_ERROR, error);

    mboxLocations.forEach(mboxLocation => {
      const requestError = error as { message?: string; stack?: string };
      adobeTargetActivityResponseMap[mboxLocation] = {
        errorMessage: requestError?.message ?? 'Unknown Adobe Target error',
        errorDetail: requestError?.message ?? String(error),
        executionTime
      };
    });
  }

  const totalExecutionTime = Date.now() - requestAdobeTargetActivityStart;
  logger.info(`Adobe Target Activity request execution time :  ${totalExecutionTime}ms`);

  return Object.keys(adobeTargetActivityResponseMap).length ? adobeTargetActivityResponseMap : undefined;
};

const hasUrlInternal = (experienceContent?: Entry<any>[]): Entry<any> | undefined =>
  experienceContent?.find((experienceEntry: Entry<any>) => experienceEntry.sys?.contentType?.sys?.id === 'urlInternal');

// return the experience section from the activity that matches the returned value from Adobe
export const getExperienceSections = ({
  adobeTargetActivity,
  adobeTargetActivityResponse,
  forceControlExperience = false
}: IGetExperienceSections): Entry<any>[] | undefined => {
  // if a target activity hasn't been found return the control experience

  const showControlExperience =
    forceControlExperience || !adobeTargetActivityResponse || isIAdobeTargetActivityError(adobeTargetActivityResponse);

  if (showControlExperience) {
    return adobeTargetActivity.experiences[0].experienceContent;
  }

  const firstMbox = adobeTargetActivityResponse.execute?.mboxes?.[0];
  const options = firstMbox?.options;

  // if there are no options then return the default experience
  // this is a fallback in case the Adobe Target activity is not active
  if (!options) {
    return adobeTargetActivity.experiences[0].experienceContent;
  }

  // find the experience in the activity that matches the returned value from Adobe
  const matchedExperienceContent = adobeTargetActivity.experiences.find(
    (experience: IAdobeTargetExperienceContent) => experience.experienceName === options[0].content.experienceName
  )?.experienceContent;

  // Prefer the experience Adobe matched by name.
  if (matchedExperienceContent) {
    return matchedExperienceContent;
  }

  // No match: fall back to the first (default) experience, unless it's a redirect
  // (urlInternal) — in which case return nothing to avoid an unintended redirect.
  const firstExperienceContent = adobeTargetActivity.experiences[0].experienceContent;
  const firstExperienceHasUrlInternal = hasUrlInternal(firstExperienceContent);
  return firstExperienceHasUrlInternal ? undefined : firstExperienceContent;
};

export const updateSectionsWithAdobeTargetActivity = ({
  adobeTargetActivityResponses,
  adobeTargetActivityResponse,
  unparsedPageEntry,
  forceControlExperience = false,
  isAutoTest
}: IUpdateSectionsWithAdobeTargetActivity): IUpdateSectionsWithAdobeTargetActivityResult => {
  const activityResponses = adobeTargetActivityResponses ?? adobeTargetActivityResponse;
  const unparsedEntries = unparsedPageEntry?.fields.sections ?? [];

  let redirect: IAdobeTargetRedirect | undefined;
  const parsedEntries: Entry<any>[] = [];

  // `some` stops iterating as soon as an entry resolves to a redirect (the page is never rendered).
  unparsedEntries.some((entry: Entry<any>) => {
    if (entry.sys.contentType.sys.id !== 'adobeTargetActivity') {
      parsedEntries.push(entry);
      return false;
    }

    // Drop target activity entries without an mBoxName; they would break parsing.
    if (entry.fields.mBoxName === undefined) {
      return false;
    }

    const experienceContent = getExperienceSections({
      adobeTargetActivity: FAdobeTargetActivity(entry),
      adobeTargetActivityResponse: activityResponses?.[entry.fields.mBoxName],
      forceControlExperience
    });

    // A `urlInternal` experience is a redirect target, not a renderable entry.
    const urlInternalEntry = hasUrlInternal(experienceContent);
    if (urlInternalEntry) {
      if (isAutoTest) {
        return false;
      }

      redirect = { url: urlInternalEntry.fields.url, mBoxName: entry.fields.mBoxName };
      return true;
    }

    if (experienceContent) {
      parsedEntries.push(...experienceContent);
    }

    return false;
  });

  return {
    pageEntry: {
      ...(unparsedPageEntry as Entry<any>),
      fields: {
        ...(unparsedPageEntry?.fields ?? {}),
        sections: parsedEntries
      }
    },
    redirect
  };
};

export const findActivitySections = (unparsedSections: Entry<any>[]): Entry<any>[] => {
  return unparsedSections.filter(
    (section: Entry<any>) =>
      section.sys.contentType.sys.id === 'adobeTargetActivity' && section.fields.mBoxName !== undefined
  );
};
